import { useDispatch, useReduxState } from 'hooks';
import { generateICalFile } from 'lib/calendar';
import { getFormattedDate } from 'lib/common';
import { Dropdown, DropdownButton } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';

import type { CalendarEventExtended } from 'shared-types';

interface Props {
  event: CalendarEventExtended;
}

const CalendarEventAddToMyCalendar = ({ event }: Props) => {
  const { dispatch } = useDispatch();

  const { messages, theme } = useReduxState();

  const handleClick = async (option: 'google' | 'outlook' | 'other') => {
    try {
      const link = document.createElement('a');
      // storing description/link for displaying if not null
      const eventDetailsParts = [];

      if (event.description) {
        eventDetailsParts.push(event.description);
      }
      if (event.link) {
        eventDetailsParts.push(
          `<br><a href=${event.link}>${event.linkText || event.link}</a>`
        );
      }
      const eventDetails = eventDetailsParts.join('\n');
      if (option === 'google') {
        link.href =
          'https://calendar.google.com/calendar/render' +
          '?action=TEMPLATE' +
          `&text=${event.title}` +
          `&dates=${getFormattedDate({
            date: event.startDate,
            dateFormat: "YMMdd'T'HHmmss"
          })}/${getFormattedDate({
            date: event.endDate,
            dateFormat: "YMMdd'T'HHmmss"
          })}` +
          `&details=${eventDetails || ''}` +
          `&location=${event.location || ''}`;
        link.rel = 'noreferrer noopener';
        link.target = '_blank';
      } else if (option === 'outlook') {
        link.href =
          'https://outlook.office.com/calendar/deeplink/compose' +
          `?subject=${event.title}` +
          `&body=${eventDetails || ''}` +
          `&startdt=${event.startDate}` +
          `&enddt=${event.endDate}` +
          `&location=${event.location}`;
        link.rel = 'noreferrer noopener';
        link.target = '_blank';
      } else {
        const blob = generateICalFile(event);
        const fileUrl = window.URL.createObjectURL(blob);
        link.href = fileUrl;
        link.setAttribute('download', `${event.title}.ics`);
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <Dropdown
      arrowStyle={{ fontSize: '1.1rem', marginLeft: 7 }}
      buttonStyle={{
        backgroundColor: 'transparent',
        color: theme.linkColor,
        fontSize: '1.5rem',
        fontWeight: '600',
        padding: 0
      }}
      category="transparent"
      showArrow={true}
      style={{ width: 'max-content' }}
      styleContentButtons={false}
      title={messages.eventCalendarAddToCalendar}
    >
      <DropdownButton
        onClick={() => handleClick('outlook')}
        text={messages.eventCalendarAddToCalendarOutlook}
      />

      <DropdownButton
        onClick={() => handleClick('google')}
        text={messages.eventCalendarAddToCalendarGoogle}
      />

      <DropdownButton
        onClick={() => handleClick('other')}
        text={
          <span style={{ whiteSpace: 'nowrap' }}>
            {messages.eventCalendarAddToCalendarICal}

            <span
              aria-label={messages.eventCalendarAddToCalendarICalTooltip}
              data-tooltip={messages.eventCalendarAddToCalendarICalTooltip}
              style={{
                color: theme.textColorLight,
                marginLeft: 7,
                position: 'relative',
                top: 1
              }}
            >
              <Icon icon="question-circle" />
            </span>
          </span>
        }
      />
    </Dropdown>
  );
};

export default CalendarEventAddToMyCalendar;
